/**
 * MEVIA v5.0 - D12: Dashboard Central Integrado Dinámico
 * 
 * Dynamic widget rendering based on subscription plan + cycle status.
 * Priority Alerts Zone (P1-P5) + Conditional Widgets (W01-W20).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Activity, AlertTriangle, TrendingUp, TrendingDown, CheckCircle2,
  Clock, Users, RefreshCw, Loader2, ChevronRight, ExternalLink,
  Target, BarChart3, Calendar, Zap, AlertCircle, Bell, Lock,
  Shield, XCircle, FileText, Play
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';


const TEXTS = {
  es: {
    title: 'Dashboard Central',
    subtitle: 'Vista unificada de tu organizacion',
    refresh: 'Actualizar',
    lastUpdate: 'Ultima actualizacion',
    zone1Title: 'Score de Salud Organizacional',
    meviaScore: 'SCORE MEVIA',
    healthyRetention: 'Retencion Saludable',
    evaluated: 'evaluados',
    activeCycle: 'Ciclo activo',
    invitePending: 'Invitar pendientes',
    notConfigured: 'No configurado',
    SALUDABLE: 'Saludable', MODERADO: 'Moderado', ALTO_RIESGO: 'Alto Riesgo', CRITICO: 'Critico',
    zone2Title: 'Alertas Prioritarias',
    noAlerts: 'Sin alertas activas',
    zone3Title: 'Planes de Accion',
    activePlans: 'planes activos',
    overdueTasks: 'tareas vencidas',
    avgProgress: 'progreso promedio',
    urgentPlans: 'Planes mas urgentes',
    daysLeft: 'dias',
    viewPlans: 'Ver planes',
    zone4Title: 'Evolucion vs Periodo Anterior',
    deltaScore: 'puntos vs',
    employeesRetained: 'empleados mejoraron de critico',
    estimatedROI: 'Ahorro estimado',
    viewAnalysis: 'Ver analisis completo',
    completeFirstCycle: 'Completa tu primer ciclo de evaluacion para ver la evolucion',
    quickActions: 'Acciones Rapidas',
    viewAll: 'Ver todas',
    readOnlyMode: 'Modo solo lectura',
    readOnlyDesc: 'El ciclo ha vencido. Los datos se muestran en modo historico.',
    noCycleTitle: 'Sin ciclo activo',
    noCycleDesc: 'Crea tu primer ciclo de evaluacion para comenzar.',
    createCycle: 'Crear Ciclo',
    resolveAlert: 'Resolver',
    dismiss: 'Descartar'
  },
  en: {
    title: 'Central Dashboard',
    subtitle: 'Unified view of your organization',
    refresh: 'Refresh',
    lastUpdate: 'Last update',
    zone1Title: 'Organizational Health Score',
    meviaScore: 'MEVIA SCORE',
    healthyRetention: 'Healthy Retention',
    evaluated: 'evaluated',
    activeCycle: 'Active cycle',
    invitePending: 'Invite pending',
    notConfigured: 'Not configured',
    SALUDABLE: 'Healthy', MODERADO: 'Moderate', ALTO_RIESGO: 'High Risk', CRITICO: 'Critical',
    zone2Title: 'Priority Alerts',
    noAlerts: 'No active alerts',
    zone3Title: 'Action Plans',
    activePlans: 'active plans',
    overdueTasks: 'overdue tasks',
    avgProgress: 'avg progress',
    urgentPlans: 'Most urgent plans',
    daysLeft: 'days',
    viewPlans: 'View plans',
    zone4Title: 'Evolution vs Previous Period',
    deltaScore: 'points vs',
    employeesRetained: 'employees improved from critical',
    estimatedROI: 'Estimated savings',
    viewAnalysis: 'View full analysis',
    completeFirstCycle: 'Complete your first evaluation cycle to see evolution',
    quickActions: 'Quick Actions',
    viewAll: 'View all',
    readOnlyMode: 'Read-only mode',
    readOnlyDesc: 'The cycle has expired. Data is shown in historical mode.',
    noCycleTitle: 'No active cycle',
    noCycleDesc: 'Create your first evaluation cycle to get started.',
    createCycle: 'Create Cycle',
    resolveAlert: 'Resolve',
    dismiss: 'Dismiss'
  }
};

// Score Gauge
const ScoreGauge = ({ score, level, color, size = 'large' }) => {
  const radius = size === 'large' ? 80 : 50;
  const strokeWidth = size === 'large' ? 12 : 8;
  const circumference = Math.PI * radius;
  const progress = (score / 100) * circumference;
  
  return (
    <div className="relative flex items-center justify-center">
      <svg width={radius * 2 + strokeWidth * 2} height={radius + strokeWidth * 2} className="transform -rotate-180">
        <path d={`M ${strokeWidth} ${radius + strokeWidth} A ${radius} ${radius} 0 0 1 ${radius * 2 + strokeWidth} ${radius + strokeWidth}`}
          fill="none" stroke="#E5E7EB" strokeWidth={strokeWidth} strokeLinecap="round" />
        <path d={`M ${strokeWidth} ${radius + strokeWidth} A ${radius} ${radius} 0 0 1 ${radius * 2 + strokeWidth} ${radius + strokeWidth}`}
          fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"
          strokeDasharray={`${progress} ${circumference}`} className="transition-all duration-1000 ease-out" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center pt-4">
        <span className={`font-bold ${size === 'large' ? 'text-4xl' : 'text-2xl'}`} style={{ color }}>{score.toFixed(1)}</span>
        <Badge className="mt-1" style={{ backgroundColor: color, color: 'white' }}>{level}</Badge>
      </div>
    </div>
  );
};

// Priority Alert Component
const PriorityAlert = ({ alert, lang, onResolve, onDismiss }) => {
  const colorClasses = {
    red: 'bg-red-50 border-red-500 text-red-800',
    orange: 'bg-orange-50 border-orange-500 text-orange-800',
    yellow: 'bg-amber-50 border-amber-400 text-amber-800',
    blue: 'bg-blue-50 border-blue-500 text-blue-800'
  };
  const iconMap = {
    red: <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />,
    orange: <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0" />,
    yellow: <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />,
    blue: <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
  };

  return (
    <div className={`p-4 rounded-lg border-l-4 ${colorClasses[alert.color] || colorClasses.blue}`} data-testid={`alert-${alert.id}`}>
      <div className="flex items-start gap-3">
        {iconMap[alert.color] || iconMap.blue}
        <div className="flex-1">
          <p className="font-semibold text-sm">{lang === 'es' ? alert.title_es : alert.title_en}</p>
          <p className="text-sm mt-1 opacity-80">{lang === 'es' ? alert.message_es : alert.message_en}</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {alert.dismissible && (
            <Button variant="ghost" size="sm" onClick={() => onDismiss?.(alert.id)} className="text-xs">{lang === 'es' ? 'Descartar' : 'Dismiss'}</Button>
          )}
          {alert.action && (
            <Button variant="outline" size="sm" onClick={() => onResolve?.(alert)} className="text-xs">
              {lang === 'es' ? 'Resolver' : 'Resolve'} <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Component
export default function DashboardCentral({ empresaId }) {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const t = TEXTS[language] || TEXTS.es;
  const lang = language || 'es';
  
  const effectiveEmpresaId = empresaId || user?.empresa_id;
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [d12Data, setD12Data] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);

  const loadDashboard = useCallback(async (showRefreshing = false) => {
    if (!effectiveEmpresaId) return;
    
    try {
      if (showRefreshing) setRefreshing(true);
      else setLoading(true);
      
      // Load D12 dynamic data + existing dashboard data in parallel
      const [d12Res, dashRes] = await Promise.all([
        axios.get(`/v5/notifications/admin-dashboard-data/${effectiveEmpresaId}`),
        axios.get(`/v4/dashboard/full/${effectiveEmpresaId}?lang=${language}`).catch(() => null)
      ]);
      
      if (d12Res.data.success) {
        setD12Data(d12Res.data.data);
      }
      
      if (dashRes?.data?.success) {
        const data = dashRes.data.data;
        // D6: Override IPM
        try {
          const ipmRes = await axios.get(`/v5/pulso/${effectiveEmpresaId}/ipm-actual`);
          if (ipmRes.data.success && ipmRes.data.data) {
            const { ipm_actual, ipm_anterior, ciclo_id } = ipmRes.data.data;
            if (data.zone1) {
              data.zone1.ipm = { value: ipm_actual, available: ipm_actual != null, anterior: ipm_anterior, ciclo_id };
            }
          }
        } catch (e) { /* non-critical */ }
        setDashboardData(data);
      }
    } catch (error) {
      logger.error('Error loading dashboard:', error);
      toast.error(lang === 'es' ? 'Error al cargar dashboard' : 'Error loading dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [effectiveEmpresaId, language, lang]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const handleResolveAlert = (alert) => {
    switch (alert.action) {
      case 'cycle_decision': navigate('/evaluation-cycles'); break;
      case 'view_report': navigate(`/people-analytics?employee=${alert.action_data?.employee_id}`); break;
      case 'view_nom035': navigate('/nom035'); break;
      case 'manage_cycle': navigate('/evaluation-cycles'); break;
      case 'onboarding': navigate('/assessment-config?tab=onboarding'); break;
      default: break;
    }
  };

  const handleDismissAlert = (alertId) => {
    setDismissedAlerts(prev => [...prev, alertId]);
    if (alertId === 'P5') {
      axios.post(`/v5/empresa/${effectiveEmpresaId}/onboarding/dismiss-banner`).catch(() => {});
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;
  }

  const moduleAccess = d12Data?.module_access || {};
  const widgetVis = d12Data?.widget_visibility || {};
  const cycleStatus = d12Data?.cycle_status?.status || 'SIN_CICLO';
  const isReadOnly = d12Data?.is_read_only;
  const priorityAlerts = (d12Data?.priority_alerts || []).filter(a => !dismissedAlerts.includes(a.id));
  const hasBlockingAlert = priorityAlerts.some(a => a.blocking);
  const zone1 = dashboardData?.zone1;
  const zone2 = dashboardData?.zone2;
  const zone3 = dashboardData?.zone3;
  const zone4 = dashboardData?.zone4;
  const quickActions = dashboardData?.quick_actions;

  return (
    <div className="space-y-6" data-testid="dashboard-central">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t.title}</h1>
          <p className="text-gray-500">{t.subtitle}</p>
        </div>
        <div className="flex items-center gap-3">
          {isReadOnly && (
            <Badge variant="outline" className="text-amber-700 border-amber-400 bg-amber-50">
              <Lock className="w-3 h-3 mr-1" />{t.readOnlyMode}
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={() => loadDashboard(true)} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {t.refresh}
          </Button>
        </div>
      </div>

      {/* D12: Priority Alerts Zone */}
      {priorityAlerts.length > 0 && (
        <div className="space-y-3" data-testid="priority-alerts-zone">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className={`w-5 h-5 ${priorityAlerts.some(a => a.type === 'critical') ? 'text-red-500' : 'text-orange-500'}`} />
            {t.zone2Title}
            <Badge variant="destructive">{priorityAlerts.length}</Badge>
          </h2>
          {priorityAlerts.map(alert => (
            <PriorityAlert key={alert.id} alert={alert} lang={lang} onResolve={handleResolveAlert} onDismiss={handleDismissAlert} />
          ))}
        </div>
      )}

      {/* D12: Read-only overlay banner */}
      {isReadOnly && !hasBlockingAlert && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3" data-testid="read-only-banner">
          <Lock className="w-5 h-5 text-amber-600" />
          <div>
            <p className="font-medium text-amber-800">{t.readOnlyMode}</p>
            <p className="text-sm text-amber-700">{t.readOnlyDesc}</p>
          </div>
        </div>
      )}

      {/* W19: Create First Cycle CTA */}
      {widgetVis.W19_create_cycle_cta && (
        <Card className="border-2 border-dashed border-blue-300 bg-blue-50/50" data-testid="widget-W19">
          <CardContent className="py-12 text-center">
            <Play className="w-16 h-16 mx-auto text-blue-400 mb-4" />
            <h3 className="text-xl font-bold text-gray-800">{t.noCycleTitle}</h3>
            <p className="text-gray-600 mt-2 mb-6">{t.noCycleDesc}</p>
            <Button onClick={() => navigate('/assessment-config?tab=onboarding')} className="bg-blue-600 hover:bg-blue-700">
              <Calendar className="w-4 h-4 mr-2" />{t.createCycle}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Don't render dashboard content if blocking alert or no cycle */}
      {!hasBlockingAlert && !widgetVis.W19_create_cycle_cta && (
        <>
          {/* Zone 1: Health Score — only if culture module active */}
          {(widgetVis.W01_score_gauge || zone1) && (
            <Card className={`border-2 ${isReadOnly ? 'border-amber-200 bg-amber-50/20' : 'border-teal-100'}`}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Activity className="w-5 h-5 text-teal-600" />
                  {t.zone1Title}
                  {isReadOnly && <Badge variant="outline" className="text-xs text-amber-600 border-amber-300 ml-2">W20</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  {/* MEVIA Score */}
                  {widgetVis.W01_score_gauge && zone1?.mevia_score && (
                    <div className="md:col-span-2 flex flex-col items-center" data-testid="widget-W01">
                      <p className="text-sm text-gray-500 mb-2">{t.meviaScore}</p>
                      <ScoreGauge 
                        score={zone1.mevia_score.value}
                        level={t[zone1.mevia_score.level] || zone1.mevia_score.level}
                        color={zone1.mevia_score.color}
                      />
                      <p className="text-sm text-gray-600 mt-2">{t.healthyRetention}</p>
                    </div>
                  )}
                  
                  {/* Secondary metrics */}
                  <div className="space-y-4">
                    {/* IPM - only if pulse active */}
                    {widgetVis.W13_ipm_gauge && zone1?.ipm && (
                      <div className={`p-3 rounded-lg ${zone1.ipm.available ? 'bg-blue-50' : 'bg-gray-50'} cursor-pointer hover:ring-2 hover:ring-blue-200 transition-all`}
                        onClick={() => zone1.ipm.ciclo_id && navigate('/assessment-config?tab=pulse')} data-testid="widget-W13">
                        <p className="text-xs text-gray-500 uppercase">IPM</p>
                        {zone1.ipm.available ? (
                          <div className="flex items-center gap-2">
                            <p className="text-2xl font-bold text-blue-600">{zone1.ipm.value}</p>
                            {zone1.ipm.anterior != null && (() => {
                              const delta = Math.round((zone1.ipm.value - zone1.ipm.anterior) * 10) / 10;
                              return delta > 0 ? <TrendingUp className="w-4 h-4 text-green-500" /> :
                                delta < 0 ? <TrendingDown className="w-4 h-4 text-red-500" /> : null;
                            })()}
                          </div>
                        ) : <p className="text-sm text-gray-400">{t.notConfigured}</p>}
                      </div>
                    )}
                    
                    {/* IPA - only if nom035 active */}
                    {widgetVis.W09_ipa_gauge && zone1?.ipa && (
                      <div className={`p-3 rounded-lg ${zone1.ipa.available ? 'bg-green-50' : 'bg-gray-50'}`} data-testid="widget-W09">
                        <p className="text-xs text-gray-500 uppercase">IPA (NOM-035)</p>
                        {zone1.ipa.value !== null ? (
                          <p className="text-2xl font-bold text-green-600">{zone1.ipa.value}%</p>
                        ) : <p className="text-sm text-gray-400">{t.notConfigured}</p>}
                      </div>
                    )}
                  </div>
                  
                  {/* Evaluations & Cycle */}
                  <div className="space-y-4">
                    {widgetVis.W07_eval_progress && zone1?.evaluations && (
                      <div className="p-3 bg-purple-50 rounded-lg" data-testid="widget-W07">
                        <p className="text-xs text-gray-500 uppercase">{t.evaluated}</p>
                        <p className="text-2xl font-bold text-purple-600">
                          {zone1.evaluations.completed} / {zone1.evaluations.total}
                        </p>
                        <Progress value={zone1.evaluations.percentage} className="h-1.5 mt-2" />
                        {zone1.evaluations.total - zone1.evaluations.completed > 0 && (
                          <Button variant="link" size="sm" className="p-0 h-auto mt-1 text-xs"
                            onClick={() => navigate('/employees?filter=pending_evaluation')}>
                            {t.invitePending}
                          </Button>
                        )}
                      </div>
                    )}
                    
                    {widgetVis.W16_cycle_status && zone1?.active_cycle?.name && (
                      <div className="p-3 bg-gray-50 rounded-lg" data-testid="widget-W16">
                        <p className="text-xs text-gray-500 uppercase">{t.activeCycle}</p>
                        <p className="text-sm font-medium text-gray-700">{zone1.active_cycle.name}</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          {quickActions?.actions?.length > 0 && !isReadOnly && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Zap className="w-5 h-5 text-yellow-500" />{t.quickActions}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {quickActions.actions.map((action) => (
                    <button key={action.id} onClick={() => navigate(action.url)}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors w-full ${
                        action.type === 'urgent' ? 'bg-red-100 hover:bg-red-200 text-red-700 border-red-300' :
                        action.type === 'warning' ? 'bg-orange-100 hover:bg-orange-200 text-orange-700 border-orange-300' :
                        'bg-blue-100 hover:bg-blue-200 text-blue-700 border-blue-300'}`}>
                      <span className="text-xl">{action.icon}</span>
                      <div className="text-left">
                        <p className="font-medium text-sm">{action.title}</p>
                        <p className="text-xs opacity-75">{action.description}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 ml-auto" />
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Existing zone2 alerts (from original dashboard endpoint) */}
          {zone2?.alerts?.length > 0 && (
            <Card className={zone2.has_critical ? 'border-red-200 bg-red-50/30' : ''}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <AlertTriangle className={`w-5 h-5 ${zone2.has_critical ? 'text-red-500' : 'text-orange-500'}`} />
                  {lang === 'es' ? 'Alertas del Sistema' : 'System Alerts'}
                  <Badge variant="destructive">{zone2.alerts.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {zone2.alerts.map((alert, idx) => {
                    const message = lang === 'es' ? alert.message_es : alert.message_en;
                    const actionLabel = lang === 'es' ? alert.action_label_es : alert.action_label_en;
                    const colors = { critical: 'border-red-500 bg-red-50', warning: 'border-orange-500 bg-orange-50', info: 'border-blue-500 bg-blue-50' };
                    return (
                      <div key={idx} className={`p-3 rounded-lg border-l-4 ${colors[alert.severity] || colors.info}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-2">
                            <span className="text-lg">{alert.icon}</span>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{message}</p>
                              {alert.count > 0 && <p className="text-xs text-gray-500">{alert.count} {lang === 'es' ? 'afectados' : 'affected'}</p>}
                            </div>
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => navigate(alert.action_url)} className="text-xs">
                            {actionLabel}<ChevronRight className="w-3 h-3 ml-1" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Grid for Zone 3 and Zone 4 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Zone 3: Action Plans - only if module active */}
            {(widgetVis.W17_action_plans || zone3) && (
              <Card data-testid="widget-W17">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Target className="w-5 h-5 text-blue-600" />{t.zone3Title}
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={() => navigate('/dashboard?tab=action_plans')}>
                      {t.viewPlans}<ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {zone3 ? (
                    <>
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <p className="text-2xl font-bold text-blue-600">{zone3.active_plans}</p>
                          <p className="text-xs text-gray-500">{t.activePlans}</p>
                        </div>
                        <div className="text-center p-3 bg-yellow-50 rounded-lg">
                          <p className="text-2xl font-bold text-yellow-600">{zone3.overdue_tasks}</p>
                          <p className="text-xs text-gray-500">{t.overdueTasks}</p>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <p className="text-2xl font-bold text-green-600">{zone3.avg_progress}%</p>
                          <p className="text-xs text-gray-500">{t.avgProgress}</p>
                        </div>
                      </div>
                      {zone3.urgent_plans?.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">{t.urgentPlans}</p>
                          <div className="space-y-2">
                            {zone3.urgent_plans.map((plan) => (
                              <div key={plan.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                                onClick={() => navigate(`/action-plans/${plan.id}`)}>
                                <div><p className="text-sm font-medium">{plan.employee_name}</p><p className="text-xs text-gray-500">{plan.title}</p></div>
                                <Badge variant={plan.days_until < 0 ? 'destructive' : 'outline'}>{Math.abs(plan.days_until)} {t.daysLeft}</Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-400">
                      <Target className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{lang === 'es' ? 'Sin planes de accion activos' : 'No active action plans'}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Zone 4: Evolution */}
            {(widgetVis.W02_score_comparison || zone4) && (
              <Card data-testid="widget-W02">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <TrendingUp className="w-5 h-5 text-green-600" />{t.zone4Title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {zone4?.has_comparison ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg">
                        <div>
                          <p className="text-sm text-gray-500">MEVIA Score</p>
                          <p className="text-3xl font-bold" style={{ color: zone4.delta_positive ? '#22C55E' : '#EF4444' }}>
                            {zone4.delta_positive ? '+' : ''}{zone4.delta}
                          </p>
                          <p className="text-xs text-gray-500">{t.deltaScore} {zone4.previous_cycle}</p>
                        </div>
                        {zone4.delta_positive ? <TrendingUp className="w-12 h-12 text-green-500" /> : <TrendingDown className="w-12 h-12 text-red-500" />}
                      </div>
                      {zone4.employees_retained > 0 && (
                        <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                          <CheckCircle2 className="w-8 h-8 text-green-500" />
                          <div>
                            <p className="text-2xl font-bold text-green-600">{zone4.employees_retained}</p>
                            <p className="text-xs text-gray-500">{t.employeesRetained}</p>
                          </div>
                        </div>
                      )}
                      {zone4.estimated_roi > 0 && (
                        <div className="p-3 bg-purple-50 rounded-lg">
                          <p className="text-xs text-gray-500">{t.estimatedROI}</p>
                          <p className="text-2xl font-bold text-purple-600">${zone4.estimated_roi.toLocaleString()} MXN</p>
                        </div>
                      )}
                      <Button variant="outline" className="w-full" onClick={() => navigate('/evolution')}>
                        {t.viewAnalysis}<ExternalLink className="w-3 h-3 ml-2" />
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500 text-sm">{t.completeFirstCycle}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}
